import { Avatar, Col, Icon, Picture, ScrollArea, Tappable, Text, useTheme } from "@rally/ui-kit";
import { say } from "../../localization/say.ts";
import { calledBy } from "../../me/called.ts";
import { useFace } from "../../me/face.ts";
import { usePresent } from "../../push/here.ts";
import { glyphFor, sizeSaid } from "../attachment/filing.ts";
import { useLinkPreview } from "../attachment/link-preview.ts";
import { linkFace, type Offer } from "../message/tags.ts";

const A_ROW_IS = 44;

const AT_MOST_TALL = A_ROW_IS * 5.5;

function linesOf(offer: Offer): { title: string; under: string } {
  if (offer.kind === "file") {
    const { name, bytes } = offer.file;
    return { title: name, under: bytes === undefined ? "" : sizeSaid(bytes) };
  }
  if (offer.kind === "url") {
    const { host, tail } = linkFace(offer.href);
    return { title: host, under: tail };
  }
  const who = offer.who;
  return {
    title: calledBy(who) ?? who.handle,
    under: `@${who.handle}${who.kind === "agent" ? ` · ${who.here ? say("mention.agent") : say("mention.willAdd")}` : ""}`,
  };
}

function UrlRowMark({ href, conversation }: { href: string; conversation?: string }) {
  const theme = useTheme();
  const preview = useLinkPreview(conversation, href);
  return (
    <Col style={{ width: 24, height: 24, alignItems: "center", justifyContent: "center" }}>
      {preview?.favicon ? (
        <Picture
          source={{ uri: preview.favicon }}
          testID="mention-url-favicon"
          style={{ width: 18, height: 18, borderRadius: theme.radius.xs }}
        />
      ) : (
        <Icon name="globe" size={18} color="text.tertiary" />
      )}
    </Col>
  );
}

function RowMark({ offer, conversation }: { offer: Offer; conversation?: string }) {
  const who = offer.kind === "person" || offer.kind === "agent" ? offer.who : null;
  const face = useFace(who?.avatar ?? null);

  const present = usePresent(who?.account);
  if (who) {
    return (
      <Avatar
        name={calledBy(who) ?? who.handle}
        src={face ?? undefined}
        size={24}
        tone={who.kind === "agent" ? "accent" : "neutral"}
        present={present}
      />
    );
  }
  if (offer.kind === "url") return <UrlRowMark href={offer.href} conversation={conversation} />;
  if (offer.kind === "file") {
    return (
      <Col style={{ width: 24, height: 24, alignItems: "center", justifyContent: "center" }}>
        <Icon name={glyphFor(offer.file)} size={18} color="text.tertiary" />
      </Col>
    );
  }
  return null;
}

export function rowName(offer: Offer): string {
  if (offer.kind === "file") return offer.file.id;
  if (offer.kind === "url") return offer.href;
  return offer.who.handle;
}

function One({
  offer,
  active,
  onChoose,
  busy,
  conversation,
}: {
  offer: Offer;
  active: boolean;
  onChoose: () => void;
  busy: boolean;
  conversation?: string;
}) {
  const theme = useTheme();
  const { title, under } = linesOf(offer);
  return (
    <Tappable
      onPress={onChoose}
      accessibilityRole="button"
      accessibilityLabel={title}
      testID={`mention-${rowName(offer)}`}
      {...({ onMouseDown: (event: { preventDefault: () => void }) => event.preventDefault() } as object)}
      style={{
        minHeight: A_ROW_IS,
        flexDirection: "row",
        alignItems: "center",
        gap: theme.space[3],
        paddingHorizontal: theme.space[3],
        paddingVertical: theme.space[2],
        backgroundColor: active ? theme.color["bg.surfaceAlt"] : "transparent",
        opacity: busy ? 0.5 : 1,
      }}
    >
      <RowMark offer={offer} conversation={conversation} />
      <Col style={{ flex: 1, minWidth: 0 }}>
        <Text typeName="body" numberOfLines={1}>
          {title}
        </Text>
        {under ? (
          <Text typeName="caption" color="text.tertiary" numberOfLines={1}>
            {under}
          </Text>
        ) : null}
      </Col>
      <Text typeName="caption" color="text.tertiary">
        {say(
          offer.kind === "file"
            ? "tag.kind.file"
            : offer.kind === "url"
              ? "tag.kind.url"
              : offer.kind === "agent"
                ? "tag.kind.agent"
                : "tag.kind.person",
        )}
      </Text>
    </Tappable>
  );
}

export function MentionList({
  offered,
  active,
  onChoose,
  busy,
  wrong,
  conversation,
}: {
  offered: readonly Offer[];

  active: number;
  onChoose: (offer: Offer) => void;

  busy: string | null;

  wrong: string | null;
  conversation?: string;
}) {
  const theme = useTheme();
  return (
    <Col
      testID="mention-list"
      style={{
        position: "absolute",

        bottom: "100%",
        left: 0,
        right: 0,
        marginBottom: theme.space[2],
        borderWidth: 1,
        borderColor: theme.color["border.default"],
        backgroundColor: theme.color["bg.surface"],
        borderRadius: theme.radius.lg,
        overflow: "hidden",
        zIndex: 10,
        ...theme.elevation.raised,
      }}
    >
      {wrong ? (
        <Col style={{ paddingHorizontal: theme.space[3], paddingVertical: theme.space[2] }}>
          <Text typeName="caption" color="danger" testID="mention-refused">
            {wrong}
          </Text>
        </Col>
      ) : null}
      <ScrollArea style={{ maxHeight: AT_MOST_TALL }} keyboardShouldPersistTaps="always">
        {offered.map((offer, index) => (
          <One
            key={offer.key}
            offer={offer}
            active={index === active}
            busy={busy === rowName(offer)}
            conversation={conversation}
            onChoose={() => onChoose(offer)}
          />
        ))}
      </ScrollArea>
    </Col>
  );
}
